import clsx from "clsx";
import { ChevronLeft, ChevronRight, Code2, Download, Eye, Play, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import { renderMermaid } from "@/lib/mermaid";
import type { Part } from "@/lib/types";
import { CopyButton } from "./Markdown";
import { splitThink } from "./MessageView";

export const PREVIEWABLE = new Set(["html", "svg", "mermaid", "xml"]);

export interface Artifact {
  lang: string;
  code: string;
}

interface ArtifactState {
  open: Artifact | null;
  show: (a: Artifact) => void;
  close: () => void;
}

export const useArtifacts = create<ArtifactState>((set) => ({
  open: null,
  show: (a) => set({ open: a }),
  close: () => set({ open: null }),
}));

function kind(a: Artifact): "html" | "svg" | "mermaid" {
  if (a.lang === "mermaid") return "mermaid";
  if (a.lang === "svg" || (a.lang === "xml" && a.code.trimStart().startsWith("<svg"))) return "svg";
  return "html";
}

/** The document shown in the sandboxed iframe. A Mermaid diagram comes here as its finished SVG. */
export function artifactDocument(a: Artifact, dark: boolean): string {
  const bg = dark ? "#09090b" : "#ffffff";
  const base = `<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">`;
  switch (kind(a)) {
    case "svg":
      return `<!doctype html><html><head>${base}<style>html,body{margin:0;height:100%;background:${bg}}body{display:flex;align-items:center;justify-content:center}svg{max-width:100%;max-height:100%}</style></head><body>${a.code}</body></html>`;
    default:
      if (/<html[\s>]/i.test(a.code) || /<body[\s>]/i.test(a.code) || /<!doctype/i.test(a.code)) return a.code;
      return `<!doctype html><html><head>${base}</head><body>${a.code}</body></html>`;
  }
}

/** Fenced code blocks that can be previewed, in order of appearance. */
export function collectArtifacts(messages: { role: string; parts: Part[] }[]): Artifact[] {
  const out: Artifact[] = [];
  for (const m of messages) {
    if (m.role !== "assistant") continue;
    for (const p of splitThink(m.parts)) {
      if (p.type !== "text") continue;
      for (const match of p.text.matchAll(/```([\w+-]+)[^\n]*\n([\s\S]*?)```/g)) {
        const lang = match[1].toLowerCase();
        if (PREVIEWABLE.has(lang)) out.push({ lang, code: match[2].replace(/\n$/, "") });
      }
    }
  }
  return out;
}

export function PreviewButton({ lang, code }: Artifact) {
  const show = useArtifacts((s) => s.show);
  if (!PREVIEWABLE.has(lang.toLowerCase())) return null;
  return (
    <button
      type="button"
      onClick={() => show({ lang: lang.toLowerCase(), code })}
      className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-950"
    >
      <Play size={12} /> Preview
    </button>
  );
}

function dataUrl(svg: string) {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function InlineMermaid({ code, onFail }: { code: string; onFail: () => void }) {
  const [svg, setSvg] = useState<string | null>(null);
  const dark = document.documentElement.classList.contains("dark");
  useEffect(() => {
    let alive = true;
    renderMermaid(code, dark).then(
      (r) => alive && setSvg(r),
      () => alive && onFail(),
    );
    return () => {
      alive = false;
    };
  }, [code, dark, onFail]);
  if (!svg) return <div className="h-24" />;
  return <img src={dataUrl(svg)} alt="" onError={onFail} className="mx-auto max-h-[32rem] max-w-full object-contain p-2" />;
}

/**
 * Mermaid and SVG shown in the message, or null for other code. These appear without a click, so model
 * output cannot be allowed to load anything: an address could carry the conversation away (see MarkdownImage).
 * So both end up in an <img>, where browsers run no scripts and fetch nothing.
 */
export function inlineView(lang: string, code: string, onFail: () => void) {
  const a = { lang: lang.toLowerCase(), code };
  if (!PREVIEWABLE.has(a.lang) || kind(a) === "html") return null;
  const svg = /\bxmlns=/.test(code) ? code : code.replace(/<svg\b/, '<svg xmlns="http://www.w3.org/2000/svg"');
  return (
    <button type="button" className="block w-full cursor-zoom-in" title="Open larger" onClick={() => useArtifacts.getState().show(a)}>
      {kind(a) === "mermaid" ? (
        <InlineMermaid code={code} onFail={onFail} />
      ) : (
        <img src={dataUrl(svg)} alt="" onError={onFail} className="mx-auto max-h-[32rem] w-full max-w-xl bg-white object-contain" />
      )}
    </button>
  );
}

export function ArtifactPanel({ all }: { all: Artifact[] }) {
  const { open, show, close } = useArtifacts();
  const [tab, setTab] = useState<"preview" | "code">("preview");
  const dark = document.documentElement.classList.contains("dark");

  useEffect(() => setTab("preview"), [open?.code]);
  useEffect(() => () => close(), [close]);

  const index = useMemo(() => (open ? all.findIndex((a) => a.code === open.code) : -1), [all, open]);
  const [diagram, setDiagram] = useState("");
  useEffect(() => {
    setDiagram("");
    if (open?.lang !== "mermaid") return;
    let alive = true;
    renderMermaid(open.code, dark).then(
      (svg) => alive && setDiagram(svg),
      () => alive && setDiagram("<p style='font-family:system-ui;color:#71717a'>This diagram has an error. See the Code tab.</p>"),
    );
    return () => {
      alive = false;
    };
  }, [open, dark]);
  const doc = useMemo(() => (!open ? "" : open.lang === "mermaid" ? artifactDocument({ lang: "svg", code: diagram }, dark) : artifactDocument(open, dark)), [open, diagram, dark]);
  if (!open) return null;

  const ext = kind(open) === "svg" ? "svg" : kind(open) === "mermaid" ? "mmd" : "html";
  const download = () => {
    const blob = new Blob([kind(open) === "html" ? doc : open.code], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `artifact.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  const tabBtn = (t: typeof tab, icon: React.ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => setTab(t)}
      className={clsx("flex items-center gap-1.5 rounded-md px-2 py-1 text-sm", tab === t ? "bg-zinc-200 font-medium dark:bg-zinc-800" : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100")}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <aside className="fixed inset-0 z-40 flex flex-col bg-white lg:static lg:z-auto lg:w-[45%] lg:max-w-3xl lg:min-w-[380px] lg:border-l lg:border-zinc-200 dark:bg-zinc-950 lg:dark:border-zinc-800">
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-zinc-200 px-2 dark:border-zinc-800">
        {tabBtn("preview", <Eye size={14} />, "Preview")}
        {tabBtn("code", <Code2 size={14} />, "Code")}
        {all.length > 1 && index >= 0 && (
          <div className="ml-2 flex items-center text-xs text-zinc-500">
            <button className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30 dark:hover:bg-zinc-800" disabled={index <= 0} onClick={() => show(all[index - 1])} aria-label="Previous artifact">
              <ChevronLeft size={14} />
            </button>
            <span className="tabular-nums">
              {index + 1}/{all.length}
            </span>
            <button className="rounded p-1 hover:bg-zinc-100 disabled:opacity-30 dark:hover:bg-zinc-800" disabled={index >= all.length - 1} onClick={() => show(all[index + 1])} aria-label="Next artifact">
              <ChevronRight size={14} />
            </button>
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          <CopyButton text={open.code} />
          <button className="rounded p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700" onClick={download} aria-label="Download" title="Download">
            <Download size={14} />
          </button>
          <button className="rounded p-1 text-zinc-500 hover:bg-zinc-200 dark:hover:bg-zinc-700" onClick={close} aria-label="Close preview" title="Close">
            <X size={16} />
          </button>
        </div>
      </div>
      {tab === "preview" ? (
        // No allow-same-origin: the artifact runs in an opaque origin and cannot reach this app's cookies or API.
        <iframe title="Artifact preview" sandbox="allow-scripts allow-popups allow-forms allow-modals" srcDoc={doc} className="min-h-0 flex-1 bg-white" />
      ) : (
        <pre className="min-h-0 flex-1 overflow-auto p-4 text-[13px]">
          <code>{open.code}</code>
        </pre>
      )}
    </aside>
  );
}
